import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Hospital, 
  Users, 
  Clock, 
  Shield, 
  CheckCircle, 
  UserPlus, 
  FileText, 
  BarChart, 
  UserCog, 
  History, 
  ClipboardList, 
  Calendar, 
  TrendingUp, 
  Printer, 
  CreditCard,
  Database,
  BookOpen,
  MessageCircle,
} from 'lucide-react';
import { LandingPlanHighlights } from '@/components/landing/LandingPlanHighlights';
import { getSubscriptionPlans } from '@/lib/subscription';
import type { SubscriptionPlan } from '@/lib/subscription';
import {
  DEFAULT_PLAN_FALLBACK_FEATURES,
  displayPlanFeatures,
} from '@/lib/planFeatures';

export default function Landing() {
  const [isVisible, setIsVisible] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const heroRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load subscription plans from database
    const loadPlans = async () => {
      try {
        const subscriptionPlans = await getSubscriptionPlans();
        console.log('Loaded subscription plans:', subscriptionPlans);
        setPlans(subscriptionPlans);
        if (subscriptionPlans.length === 0) {
          console.warn('No subscription plans found. Make sure plans are active in the database.');
        }
      } catch (error) {
        console.error('Error loading subscription plans:', error);
        // Still set loading to false even on error
      } finally {
        setLoadingPlans(false);
      }
    };
    
    loadPlans();
  }, []);

  useEffect(() => {
    // Fade in on mount
    setIsVisible(true);

    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          
          // Add stagger animation to feature cards
          if (entry.target === featuresRef.current) {
            const cards = entry.target.querySelectorAll('[data-feature-card]');
            cards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-scale-in');
                card.classList.remove('opacity-0');
              }, index * 50); // 50ms delay between each card
            });
          }
          
          // Add stagger animation to pricing cards
          if (entry.target === pricingRef.current) {
            const pricingCards = entry.target.querySelectorAll('[data-pricing-card]');
            pricingCards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-scale-in');
                card.classList.remove('opacity-0');
              }, index * 100); // 100ms delay between each pricing card
            });
          }
        }
      });
    }, observerOptions);

    const elements = [heroRef.current, featuresRef.current, pricingRef.current].filter(Boolean);
    elements.forEach((el) => el && observer.observe(el));

    return () => {
      elements.forEach((el) => el && observer.unobserve(el));
    };
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Attractive Background with Gradient and Patterns */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 via-white to-emerald-50"></div>
      
      {/* Animated Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 via-transparent to-emerald-400/10 animate-pulse"></div>
      
      {/* Decorative Circles with floating animation */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200/20 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-200/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-200/15 rounded-full blur-3xl animate-float" style={{ animationDelay: '4s' }}></div>
      
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
      
      {/* Content Container with relative positioning */}
      <div className="relative z-10">
      {/* Header */}
      <header className={`container mx-auto px-4 py-6 transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 animate-slide-in-left">
            <div className="relative group">
              {/* AM Logo - Blue rounded square with glow effect */}
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 shadow-lg relative overflow-hidden transition-transform duration-300 group-hover:scale-110" style={{
                boxShadow: '0 0 20px rgba(37, 99, 235, 0.6), 0 0 40px rgba(37, 99, 235, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              }}>
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-lg" style={{
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.5) 0%, rgba(37, 99, 235, 0.3) 100%)',
                  filter: 'blur(8px)',
                  transform: 'scale(1.2)'
                }}></div>
                {/* AM Text */}
                <div className="relative z-10 w-full h-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg tracking-tight" style={{
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '-0.5px'
                  }}>AM</span>
                </div>
                {/* Highlight effect */}
                <div className="absolute top-0 right-0 w-1/2 h-1/2 rounded-tl-lg bg-gradient-to-br from-white/20 to-transparent"></div>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">AM CARE</h1>
          </div>
          <div className="flex gap-3 animate-slide-in-right">
            <Button variant="outline" asChild className="transition-all duration-300 hover:scale-105">
              <Link to="/login">Login</Link>
            </Button>
            <Button asChild className="transition-all duration-300 hover:scale-105">
              <Link to="/signup">Get Started</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section ref={heroRef} className="container mx-auto px-4 py-16 text-center opacity-0">
        <h2 className="text-5xl font-bold text-gray-900 mb-4 transition-all duration-500 hover:scale-105">
          Hospital Management Made Simple
        </h2>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          Complete outpatient management system for modern healthcare facilities.
          Manage patients, queues, prescriptions, and message patients on WhatsApp — all in one platform.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Button
            size="lg"
            variant="outline"
            asChild
            className="border-indigo-300 bg-white text-indigo-800 hover:!bg-indigo-600 hover:!text-white hover:!border-indigo-600 transition-all duration-300 hover:scale-110"
          >
            <Link to="/user-guide" className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              User Guide
            </Link>
          </Button>
          <Button
            size="lg"
            asChild
            className="bg-hospital-primary hover:bg-hospital-primary/90 transition-all duration-300 hover:scale-110 hover:shadow-lg"
          >
            <Link to="/signup">Start Free Trial</Link>
          </Button>
          <Button size="lg" variant="outline" asChild className="transition-all duration-300 hover:scale-110">
            <Link to="/login">Already have an account?</Link>
          </Button>
        </div>
        <p className="text-sm text-gray-500 mt-4 animate-pulse">
          🎁 14-day free trial • No credit card required
        </p>
      </section>

      <LandingPlanHighlights />

      {/* Features */}
      <section ref={featuresRef} className="container mx-auto px-4 py-16 opacity-0">
        <h3 className="text-3xl font-bold text-center mb-4">Everything you need</h3>
        <p className="text-center text-gray-600 mb-12">All features included in every plan - no restrictions</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-blue-50 to-blue-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Database className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">Unlimited Patients</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                No limit on patient records - grow without restrictions
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-indigo-50 to-indigo-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Users className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">Unlimited Users</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Add as many staff and doctors as needed - no user limits
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-emerald-50 to-emerald-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <UserPlus className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">Patient Registration & Management</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Complete patient lifecycle management from registration to history
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-green-50 to-green-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2 ring-1 ring-green-200/60">
            <div className="absolute top-0 right-0 w-24 h-24 bg-green-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <MessageCircle className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">WhatsApp Patient Messaging</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Queue updates, follow-ups, and prescription PDFs via pre-filled WhatsApp — no API required
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-cyan-50 to-cyan-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Clock className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">Real-time Queue System</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Live queue management with instant updates across all devices
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-purple-50 to-purple-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-purple-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <FileText className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">E-Prescription Generation</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Digital prescriptions with templates and professional printing
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-orange-50 to-orange-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-orange-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <BarChart className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">Reports & Analytics</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Comprehensive analytics dashboard with revenue and patient insights
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-pink-50 to-pink-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-pink-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <UserCog className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">Doctor & Staff Management</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Multi-user support with role-based access control
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-teal-50 to-teal-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-teal-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <History className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">Patient History Tracking</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Complete medical history with visits, prescriptions, and diagnosis
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-violet-50 to-violet-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-violet-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <ClipboardList className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">Medication Templates</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Save and reuse common medication patterns for faster prescriptions
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-rose-50 to-rose-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-rose-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Calendar className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">Follow-up Management</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Schedule and track patient follow-ups with reminders
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-amber-50 to-amber-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-amber-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">Revenue Tracking</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Financial analytics with revenue insights and payment tracking
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-lime-50 to-lime-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-lime-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-lime-500 to-lime-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Printer className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">Print Prescriptions</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Professional prescription printing with clinic branding
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-sky-50 to-sky-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-sky-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <CreditCard className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">Billing & Payments</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Complete billing system with multiple payment modes and receipts
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-feature-card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-purple-50 to-purple-100/50 overflow-hidden relative group h-full opacity-0 hover:scale-105 hover:-translate-y-2">
            <div className="absolute top-0 right-0 w-24 h-24 bg-purple-400/20 rounded-full blur-2xl"></div>
            <CardHeader className="relative z-10 pb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Shield className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-lg font-bold text-gray-900 leading-tight">Secure & Isolated</CardTitle>
              <CardDescription className="text-gray-700 mt-2 text-sm leading-relaxed">
                Complete data isolation - your data is completely separate from other hospitals
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </section>

      {/* Pricing Preview */}
      <section ref={pricingRef} className="container mx-auto px-4 py-16 opacity-0">
        <h3 className="text-3xl font-bold text-center mb-4">Flexible Plans</h3>
        <p className="text-center text-gray-600 mb-12">Choose what works for your hospital</p>
        {loadingPlans ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No subscription plans available at the moment.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => {
              const features = plan.features as any;
              const rawFeatureList: string[] = features?.features || [];
              const featureList = displayPlanFeatures(rawFeatureList, 4);
              const supportType = features?.support || 'email';
              const monthlyRate = Math.round(plan.price / plan.duration_months);
              
              // Calculate savings compared to 3-month plan
              const base3MonthPlan = plans.find(p => p.duration_months === 3);
              let savings = 0;
              let discountPercent = 0;
              if (base3MonthPlan && plan.duration_months > 3) {
                const equivalentPrice = (base3MonthPlan.price / 3) * plan.duration_months;
                savings = equivalentPrice - plan.price;
                discountPercent = Math.round((savings / equivalentPrice) * 100);
              }
              
              const isPopular = plan.duration_months === 6;
              const isBestValue = plan.duration_months === 12;
              
              // Determine card styling based on plan
              const cardClasses = isPopular
                ? "border-2 border-blue-500 shadow-2xl hover:shadow-3xl transition-all duration-300 bg-gradient-to-br from-blue-50 via-blue-100/50 to-indigo-50 overflow-hidden relative group scale-105 opacity-0 hover:scale-110"
                : isBestValue
                ? "border-2 border-emerald-500 shadow-2xl hover:shadow-3xl transition-all duration-300 bg-gradient-to-br from-emerald-50 via-emerald-100/50 to-green-50 overflow-hidden relative group opacity-0 hover:scale-105 hover:-translate-y-2"
                : "border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-gray-50 to-gray-100/50 overflow-hidden relative group opacity-0 hover:scale-105 hover:-translate-y-2";
              
              const priceColor = isPopular ? "text-blue-700" : isBestValue ? "text-emerald-700" : "text-gray-900";
              const badgeBg = isPopular 
                ? "bg-gradient-to-r from-blue-600 to-blue-700"
                : isBestValue
                ? "bg-gradient-to-r from-emerald-600 to-green-700"
                : "";
              
              const blurClass = isPopular || isBestValue ? 'blur-3xl' : 'blur-2xl';
              const bgSize = isPopular || isBestValue ? 'w-32 h-32' : 'w-24 h-24';
              const bgColor = isPopular ? 'bg-blue-400/30' : isBestValue ? 'bg-emerald-400/30' : 'bg-gray-300/20';
              
              return (
                <Card key={plan.id} data-pricing-card className={cardClasses}>
                  <div className={`absolute top-0 right-0 ${bgSize} ${bgColor} rounded-full ${blurClass}`}></div>
                  {(isPopular || isBestValue) && (
                    <div className="absolute top-4 right-4">
                      <Badge className={`${badgeBg} text-white border-0 shadow-lg px-3 py-1 text-sm font-semibold`}>
                        {isPopular ? 'Popular' : 'Best Value'}
                      </Badge>
                    </div>
                  )}
                  <CardHeader className={`relative z-10 pb-4 ${isPopular || isBestValue ? 'pt-8' : ''}`}>
                    <CardTitle className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</CardTitle>
                    <div className="mb-2">
                      <span className={`text-3xl font-bold ${priceColor}`}>
                        ₹{plan.price.toLocaleString('en-IN')}
                      </span>
                      {savings > 0 && (
                        <span className="ml-2 text-sm font-semibold text-green-600 bg-green-100 px-2 py-1 rounded">
                          Save ₹{savings.toLocaleString('en-IN')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-gray-700">₹{monthlyRate.toLocaleString('en-IN')}/month</p>
                  </CardHeader>
                  <CardContent className="relative z-10">
                    <ul className="space-y-3">
                      {featureList.length > 0 ? (
                        featureList.map((feature: string, idx: number) => (
                          <li key={idx} className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full ${isBestValue ? 'bg-gradient-to-br from-green-500 to-emerald-600' : isPopular ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-green-500'} flex items-center justify-center flex-shrink-0 ${isBestValue ? 'shadow-md transition-transform duration-300 hover:scale-110' : isPopular ? 'shadow-md' : 'transition-transform duration-300 hover:scale-110'}`}>
                              <CheckCircle className="h-4 w-4 text-white" />
                            </div>
                            <span className={`text-gray-700 ${isPopular || isBestValue ? 'font-medium' : ''}`}>
                              {feature}
                            </span>
                          </li>
                        ))
                      ) : (
                        DEFAULT_PLAN_FALLBACK_FEATURES.map((feature, idx) => {
                          const label =
                            feature === 'Email Support'
                              ? supportType === 'priority'
                                ? 'Priority Support'
                                : supportType === '24/7'
                                  ? '24/7 Support'
                                  : 'Email Support'
                              : feature;
                          return (
                          <li key={idx} className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full ${isBestValue ? 'bg-gradient-to-br from-green-500 to-emerald-600' : isPopular ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-green-500'} flex items-center justify-center flex-shrink-0 ${isBestValue ? 'shadow-md transition-transform duration-300 hover:scale-110' : isPopular ? 'shadow-md' : 'transition-transform duration-300 hover:scale-110'}`}>
                              <CheckCircle className="h-4 w-4 text-white" />
                            </div>
                            <span className={`text-gray-700 ${isPopular || isBestValue ? 'font-medium' : ''}`}>
                              {label}
                            </span>
                          </li>
                          );
                        })
                      )}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-16 text-center">
        <h3 className="text-3xl font-bold mb-4">Ready to get started?</h3>
        <p className="text-gray-600 mb-8">Join hospitals already using AM CARE</p>
        <Button size="lg" asChild>
          <Link to="/signup">Start Your Free Trial</Link>
        </Button>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t mt-16">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="relative">
                  {/* AM Logo - Blue rounded square with glow effect */}
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 shadow-lg relative overflow-hidden" style={{
                    boxShadow: '0 0 15px rgba(37, 99, 235, 0.5), 0 0 30px rgba(37, 99, 235, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                  }}>
                    {/* Glow effect */}
                    <div className="absolute inset-0 rounded-lg" style={{
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.4) 0%, rgba(37, 99, 235, 0.2) 100%)',
                      filter: 'blur(6px)',
                      transform: 'scale(1.2)'
                    }}></div>
                    {/* AM Text */}
                    <div className="relative z-10 w-full h-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm tracking-tight" style={{
                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                        letterSpacing: '-0.3px'
                      }}>AM</span>
                    </div>
                    {/* Highlight effect */}
                    <div className="absolute top-0 right-0 w-1/2 h-1/2 rounded-tl-lg bg-gradient-to-br from-white/20 to-transparent"></div>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900">AM CARE</h3>
              </div>
              <p className="text-sm text-gray-600">
                Complete hospital management system for modern healthcare facilities.
              </p>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/terms-and-conditions" className="text-gray-600 hover:text-blue-600 transition-colors">
                    Terms & Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/privacy-policy" className="text-gray-600 hover:text-blue-600 transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/refund-policy" className="text-gray-600 hover:text-blue-600 transition-colors">
                    Refund Policy
                  </Link>
                </li>
                <li>
                  <Link to="/shipping-policy" className="text-gray-600 hover:text-blue-600 transition-colors">
                    Shipping Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Support Links */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/contact-us" className="text-gray-600 hover:text-blue-600 transition-colors">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <a href="mailto:support@amcares.in" className="text-gray-600 hover:text-blue-600 transition-colors">
                    support@amcares.in
                  </a>
                </li>
              </ul>
            </div>

            {/* Quick Actions */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Get Started</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/signup" className="text-gray-600 hover:text-blue-600 transition-colors">
                    Sign Up
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="text-gray-600 hover:text-blue-600 transition-colors">
                    Login
                  </Link>
                </li>
                <li>
                  <Link to="/user-guide" className="text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-1">
                    <BookOpen className="h-4 w-4" />
                    User Guide
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t pt-8 text-center text-sm text-gray-600">
            <p>&copy; {new Date().getFullYear()} AM CARE. All rights reserved.</p>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}

